class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slider-slide]')];
    this.dots = [...this.querySelectorAll('[data-slider-dot]')];
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Number(this.dataset.interval) || 5000;
    this.readyToPlay = false;
    this.inView = false;
    this.requestId = 0;

    this.querySelector('[data-slider-previous]')?.addEventListener('click', this.previous);
    this.querySelector('[data-slider-next]')?.addEventListener('click', this.next);
    this.dots.forEach((dot) => dot.addEventListener('click', this.select));
    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.play);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.play);
    this.addEventListener('touchstart', this.touchStart, { passive: true });
    this.addEventListener('touchend', this.touchEnd, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.inView ? this.play() : this.pause();
    });
    this.observer.observe(this);
    this.prepareSlide(this.currentIndex).then(() => {
      if (!this.isConnected) return;
      this.readyToPlay = true;
      this.prepareSlide((this.currentIndex + 1) % this.slides.length);
      this.play();
    });
  }

  disconnectedCallback() {
    this.pause();
    this.requestId++;
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  prepareSlide(index) {
    const images = [...(this.slides[index]?.querySelectorAll('img') || [])];
    return Promise.all(images.filter((image) => image.getClientRects().length && image.clientWidth > 0).map((image) => {
      // Fetch the next slide once the first is ready, and decode before displaying it.
      image.loading = 'eager';
      return image.decode().catch(() => {});
    }));
  }

  async goTo(index) {
    if (this.slides.length < 2) return;
    const nextIndex = (index + this.slides.length) % this.slides.length;
    const requestId = ++this.requestId;
    await this.prepareSlide(nextIndex);
    if (!this.isConnected || requestId !== this.requestId) return;
    this.currentIndex = nextIndex;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.currentIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      const video = slide.querySelector('video');
      if (video) active ? video.play().catch(() => {}) : video.pause();
    });
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.currentIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
    this.prepareSlide((this.currentIndex + 1) % this.slides.length);
  }

  previous = () => { this.goTo(this.currentIndex - 1); this.restart(); };
  next = () => { this.goTo(this.currentIndex + 1); this.restart(); };
  select = (event) => { this.goTo(Number(event.currentTarget.dataset.sliderDot)); this.restart(); };
  pause = () => window.clearInterval(this.timer);
  play = () => {
    this.pause();
    if (this.readyToPlay && this.inView && this.dataset.autoplay === 'true' && this.slides.length > 1 && !document.hidden) {
      this.timer = window.setInterval(() => this.goTo(this.currentIndex + 1), this.interval);
    }
  };
  restart = () => { if (this.dataset.autoplay === 'true') this.play(); };
  handleVisibility = () => document.hidden ? this.pause() : this.play();
  touchStart = (event) => { this.touchStartX = event.changedTouches[0]?.clientX ?? 0; };
  touchEnd = (event) => {
    const distance = (event.changedTouches[0]?.clientX ?? this.touchStartX) - this.touchStartX;
    if (Math.abs(distance) < 45) return;
    distance > 0 ? this.previous() : this.next();
  };
}

if (!customElements.get('hero-slider')) customElements.define('hero-slider', HeroSlider);
